import { ChangeDetectionStrategy, Component } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-ementa-section',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <section class="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto space-y-10">
      <!-- Document Header -->
      <div class="bg-[#121214] rounded-2xl p-6 sm:p-8 border border-white/10 shadow-xl relative overflow-hidden">
        <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
          <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-semibold border border-[#F27D26]/30">
            <span class="material-symbols-outlined text-sm">auto_stories</span>
            <span>Plano de Ensino & Ementa</span>
          </span>
        </div>

        <h2 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight mb-2">
          {{ ementaData.componente }}
        </h2>
        
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6 pt-6 border-t border-white/10">
          <div class="p-4 rounded-xl bg-[#0A0A0B] border border-white/10 flex items-center space-x-3">
            <div class="p-2.5 rounded-lg bg-[#F27D26]/10 text-[#F27D26] border border-[#F27D26]/30">
              <span class="material-symbols-outlined text-xl">wb_sunny</span>
            </div>
            <div>
              <span class="text-[10px] font-mono font-semibold uppercase text-zinc-400 block">Turno Diurno</span>
              <p class="text-xs font-bold text-white">{{ ementaData.diurno }}</p>
            </div>
          </div>

          <div class="p-4 rounded-xl bg-[#0A0A0B] border border-white/10 flex items-center space-x-3">
            <div class="p-2.5 rounded-lg bg-[#F27D26]/10 text-[#F27D26] border border-[#F27D26]/30">
              <span class="material-symbols-outlined text-xl">bedtime</span>
            </div>
            <div>
              <span class="text-[10px] font-mono font-semibold uppercase text-zinc-400 block">Turno Noturno</span>
              <p class="text-xs font-bold text-white">{{ ementaData.noturno }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Competências & Habilidades Grid -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <!-- Competências -->
        <div class="bg-[#121214] rounded-2xl p-6 sm:p-8 border border-white/10 shadow-xl space-y-6 flex flex-col justify-between">
          <div class="space-y-4">
            <div class="flex items-center space-x-3 border-b border-white/10 pb-4">
              <span class="material-symbols-outlined text-[#F27D26] text-2xl">psychology</span>
              <h3 class="text-xl font-bold text-white tracking-tight">Competências</h3>
            </div>

            <ul class="space-y-3">
              @for (comp of ementaData.competencias; track comp) {
                <li class="flex items-start space-x-3 p-3.5 rounded-xl bg-[#0A0A0B] border border-white/10 text-sm text-zinc-200">
                  <span class="material-symbols-outlined text-[#F27D26] text-lg shrink-0 mt-0.5">check_circle</span>
                  <span class="leading-relaxed">{{ comp }}</span>
                </li>
              }
            </ul>
          </div>
        </div>

        <!-- Habilidades -->
        <div class="bg-[#121214] rounded-2xl p-6 sm:p-8 border border-white/10 shadow-xl space-y-6 flex flex-col justify-between">
          <div class="space-y-4">
            <div class="flex items-center space-x-3 border-b border-white/10 pb-4">
              <span class="material-symbols-outlined text-[#F27D26] text-2xl">build_circle</span>
              <h3 class="text-xl font-bold text-white tracking-tight">Habilidades</h3>
            </div>

            <ul class="space-y-3">
              @for (hab of ementaData.habilidades; track hab) {
                <li class="flex items-start space-x-3 p-3.5 rounded-xl bg-[#0A0A0B] border border-white/10 text-sm text-zinc-200">
                  <span class="material-symbols-outlined text-[#F27D26] text-lg shrink-0 mt-0.5">star</span>
                  <span class="leading-relaxed">{{ hab }}</span>
                </li>
              }
            </ul>
          </div>
        </div>
      </div>

      <!-- Métodos, Avaliação e Recursos Grid -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- Métodos e Técnicas de Ensino -->
        <div class="bg-[#121214] rounded-2xl p-6 sm:p-8 border border-white/10 shadow-xl space-y-6">
          <div class="flex items-center space-x-3 border-b border-white/10 pb-4">
            <span class="material-symbols-outlined text-[#F27D26] text-2xl">school</span>
            <h3 class="text-xl font-bold text-white tracking-tight">Métodos e Técnicas de Ensino</h3>
          </div>

          <ul class="space-y-3">
            @for (metodo of ementaData.metodosLista; track metodo.item) {
              <li class="flex items-center space-x-3 p-3.5 rounded-xl bg-[#0A0A0B] border border-white/10 text-sm text-zinc-200 hover:border-[#F27D26]/40 transition-all">
                <span class="material-symbols-outlined text-[#F27D26] text-lg shrink-0">{{ metodo.icon }}</span>
                <span class="leading-relaxed font-medium">{{ metodo.item }}</span>
              </li>
            }
          </ul>
        </div>

        <!-- Critérios e Instrumentos de Avaliação -->
        <div class="bg-[#121214] rounded-2xl p-6 sm:p-8 border border-white/10 shadow-xl space-y-6">
          <div class="flex items-center space-x-3 border-b border-white/10 pb-4">
            <span class="material-symbols-outlined text-[#F27D26] text-2xl">assignment_turned_in</span>
            <h3 class="text-xl font-bold text-white tracking-tight">Critérios de Avaliação</h3>
          </div>

          <div class="space-y-3">
            @for (aval of ementaData.criteriosAvaliacao; track aval.item) {
              <div class="p-3.5 rounded-xl bg-[#0A0A0B] border border-white/10 hover:border-[#F27D26]/40 transition-all flex items-center justify-between space-x-3">
                <div class="flex items-center space-x-3">
                  <span class="material-symbols-outlined text-[#F27D26] text-lg shrink-0">{{ aval.icon }}</span>
                  <span class="text-sm font-medium text-zinc-200 leading-snug">{{ aval.item }}</span>
                </div>
                <span class="px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] border border-[#F27D26]/30 font-mono text-xs font-bold shrink-0">
                  {{ aval.pontos }}
                </span>
              </div>
            }
          </div>
        </div>

        <!-- Recursos Didáticos -->
        <div class="bg-[#121214] rounded-2xl p-6 sm:p-8 border border-white/10 shadow-xl space-y-6">
          <div class="flex items-center space-x-3 border-b border-white/10 pb-4">
            <span class="material-symbols-outlined text-[#F27D26] text-2xl">devices_other</span>
            <h3 class="text-xl font-bold text-white tracking-tight">Recursos Didáticos</h3>
          </div>

          <ul class="space-y-3">
            @for (rec of ementaData.recursosLista; track rec.item) {
              <li class="flex items-center space-x-3 p-3.5 rounded-xl bg-[#0A0A0B] border border-white/10 text-sm text-zinc-200 hover:border-[#F27D26]/40 transition-all">
                <span class="material-symbols-outlined text-[#F27D26] text-lg shrink-0">{{ rec.icon }}</span>
                <span class="leading-relaxed font-medium">{{ rec.item }}</span>
              </li>
            }
          </ul>
        </div>
      </div>

      <!-- Bases Tecnológicas -->
      <div class="bg-[#121214] rounded-2xl p-6 sm:p-8 border border-white/10 shadow-xl space-y-6">
        <div class="flex items-center space-x-3 border-b border-white/10 pb-4">
          <span class="material-symbols-outlined text-[#F27D26] text-2xl">terminal</span>
          <div>
            <h3 class="text-xl font-bold text-white tracking-tight">Bases Tecnológicas</h3>
            <p class="text-xs text-zinc-400 mt-0.5">Conteúdos programáticos e tópicos fundamentais ministrados</p>
          </div>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          @for (base of ementaData.basesTecnologicas; track base) {
            <div class="p-4 rounded-xl bg-[#0A0A0B] border border-white/10 hover:border-[#F27D26]/40 transition-all flex items-start space-x-3">
              <span class="material-symbols-outlined text-[#F27D26] text-lg shrink-0 mt-0.5">label</span>
              <span class="text-sm font-semibold text-zinc-200 leading-relaxed">{{ base }}</span>
            </div>
          }
        </div>
      </div>

      <!-- Bibliografia -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <!-- Bibliografia Básica -->
        <div class="bg-[#121214] rounded-2xl p-6 sm:p-8 border border-white/10 shadow-xl space-y-6">
          <div class="flex items-center space-x-3 border-b border-white/10 pb-4">
            <span class="material-symbols-outlined text-[#F27D26] text-2xl">menu_book</span>
            <h3 class="text-xl font-bold text-white tracking-tight">Bibliografia Básica</h3>
          </div>

          <div class="space-y-3">
            @for (bib of ementaData.bibliografiaBasica; track bib) {
              <div class="p-4 rounded-xl bg-[#0A0A0B] border border-white/10 text-sm text-zinc-300 space-y-1">
                <span class="material-symbols-outlined text-[#F27D26] text-base block mb-1">book</span>
                <p class="leading-relaxed font-mono text-xs sm:text-sm">{{ bib }}</p>
              </div>
            }
          </div>
        </div>

        <!-- Bibliografia Complementar -->
        <div class="bg-[#121214] rounded-2xl p-6 sm:p-8 border border-white/10 shadow-xl space-y-6">
          <div class="flex items-center space-x-3 border-b border-white/10 pb-4">
            <span class="material-symbols-outlined text-[#F27D26] text-2xl">library_books</span>
            <h3 class="text-xl font-bold text-white tracking-tight">Bibliografia Complementar</h3>
          </div>

          <div class="space-y-3">
            @for (bibComp of ementaData.bibliografiaComplementar; track bibComp) {
              <div class="p-4 rounded-xl bg-[#0A0A0B] border border-white/10 text-sm text-zinc-300 space-y-1">
                <span class="material-symbols-outlined text-[#F27D26] text-base block mb-1">bookmark</span>
                <p class="leading-relaxed font-mono text-xs sm:text-sm">{{ bibComp }}</p>
              </div>
            }
          </div>
        </div>
      </div>
    </section>
  `
})
export class EmentaSectionComponent {
  ementaData = {
    componente: "COMPONENTE CURRICULAR: EMPREENDEDORISMO",
    diurno: "Diurno: Mód II – Carga Horária: 40h - Aulas Semanais: 02",
    noturno: "Noturno: Mód IV – Carga Horária: 40h - Aulas Semanais: 02",
    recursosLista: [
      { item: 'Sala de Aula', icon: 'meeting_room' },
      { item: 'Laboratório de Informática', icon: 'desktop_windows' },
      { item: 'Quadro Digital', icon: 'co_present' },
      { item: 'Plataforma Moodle', icon: 'school' },
      { item: 'Quadro Branco e Pincéis', icon: 'draw' }
    ],
    criteriosAvaliacao: [
      { item: "Atividades Individuais", pontos: "40pts", icon: "person" },
      { item: "Projeto", pontos: "40pts", icon: "rocket_launch" },
      { item: "Atividades em Eventos Internos e Externos", pontos: "20pts", icon: "groups" }
    ],
    metodosLista: [
      { item: "Aulas expositivas", icon: "present_to_all" },
      { item: "Vídeos e Filmes", icon: "movie" },
      { item: "Estudo dirigido em sala", icon: "auto_stories" },
      { item: "Fóruns", icon: "forum" },
      { item: "Desenvolvimento de Projeto de Empreendedorismo (Simulação de Empresa)", icon: "lightbulb" },
      { item: "Metodologia do Projeto Despertar/SEBRAE", icon: "workspace_premium" }
    ],
    competencias: [
      "Compreender os conceitos do empreendedorismo com foco em inovação tecnológica, startups e novos modelos de negócios em tecnologia da informação e comunicação;",
      "Analisar o perfil empreendedor e o comportamento e a associar suas implicações na inovação e evolução tecnológica;",
      "Identificar oportunidades de negócios através de técnicas específicas;",
      "Reconhecer novas formas e formas de trabalho e de negócios;",
      "Entender o processo de elaboração e a importância dos modelos de negócios a partir do estudo do Business Model Generation – Canvas;",
      "Conhecer as principais partes de um Plano de Negócios;",
      "Compreender o perfil empreendedor e o comportamento e suas implicações na inovação tecnológica;",
      "Identificar novas oportunidades de negócio, desenvolvendo novos produtos e serviços a partir da necessidade do cliente.",
      "Identificar o funcionamento de novas formas de incentivo ao processo empreendedor como as Incubadoras e as Aceleradoras de empresas."
    ],
    habilidades: [
      "Elaborar um modelo de negócio para produto ou serviço em tecnologia da informação e comunicação,",
      "Posicionar-se de forma empreendedora no mercado de trabalho, utilizando as novas formas de trabalho e negócios;",
      "Atuar de forma empreendedora com o objetivo de criar novas empresas, produtos e serviços que gerem emprego e renda;",
      "Elaborar modelos de negócios e planos de negócios simplificados que resultem em novos negócios;",
      "Analisar as possibilidades de criação de um negócio sustentável a partir de uma ideia tecnológica inovadora"
    ],
    basesTecnologicas: [
      "Empreendedorismo e Empreendedor;",
      "Competência Empreendedora;",
      "Perfil Empreendedor;",
      "O comportamento empreendedor;",
      "Tipos de Empreendedor;",
      "O empreendedorismo no contexto do mercado;",
      "Empreendedorismo Social;",
      "Novas formas de trabalho e de negócios: Coworking, Economia Criativa, etc.",
      "Startups, Aceleradora, Investidores; Incubadoras;",
      "Lean Startup; MVP (“Minimum Viable Product”, ou “Produto Mínimo Viável”)",
      "Modelo de Negócios: Canvas;",
      "O plano de Negócios."
    ],
    bibliografiaBasica: [
      "CARVALHO, Humberto Gonçalves de. Empreendedorismo: como planejar e administrar seu negócio. Rio de Janeiro: Ferreira. 2009.",
      "GAUTHIER, Fernando Alvaro Osturi. Empreendedorismo. Curitiba: Livro Técnico. 2010.",
      "SERTEK, Paulo. Empreendedorismo. Curitiba: IBPEX. 2007."
    ],
    bibliografiaComplementar: [
      "BERNARDI, Luiz Antonio. Manual de empreendedorismo e gestão: fundamentos, estratégias e dinâmicas. São Paulo: Atlas. 2003.",
      "DOLABELA, Fernando. Segredo de Luísa: uma ideia, uma paixão e plano de negócios. São Paulo: Sextante, 2008.",
      "MEZRICH, Bem. Bilionários por acaso: a criação do facebook. São Paulo: Intrinseca, 2010."
    ]
  };
}
